import React, { memo, useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import classNames from 'classnames';
import { useFlags } from 'launchdarkly-react-client-sdk';
import dompurify from 'dompurify';

import Avatar from 'Shared/components/Avatar/Avatar';
import { useDeepEqualSelector } from 'Shared/utils/hooks';
import { WebNotificationType } from 'Shared/constants/types';
import VideoPlayer from 'Shared/components/VideoPlayer/VideoPlayer';

import { trackClickedNotification } from 'Shared/tracking/tracking';

import FeedItemWrapper from './FeedItemWrapper/FeedItemWrapper';
import CreateActionItemLinkContent from './CreateActionItemLinkContent/CreateActionItemLinkContent';

import './FeedItem.scss';
import { WpIcon } from 'Wpds/components/icon';

type Props = {
  webNotificationId: number;
  onCreateActionItemClick: Function;
};

const notLinkWrapperTypes = [
  WebNotificationType.draftFeedback,
  WebNotificationType.feedbackRequested,
  WebNotificationType.feedbackRequestedReminder,
];

function FeedItem({ webNotificationId, onCreateActionItemClick }: Props) {
  const { feedbackActionItem } = useFlags();
  const notification = useDeepEqualSelector(state =>
    state.web_notifications.find(({ id }) => id === webNotificationId)
  );

  // Store unread value at time of render so we can keep it in unread state even though
  // it was marked as read on the backend and in redux.
  const [read, setRead] = useState(false);
  useEffect(() => setRead(Boolean(notification.readAt)), []);

  const handleItemClick = useCallback(() => {
    trackClickedNotification(false, notification.data.type, null);
    window.parent.postMessage({ type: 'REQUEST_UNREAD_NOTIFICATIONS_COUNT' }, '*');
  }, []);

  if (!webNotificationId) return null;

  const to = new URL(notification.data.link, (window as any).location);
  const toPath = `${to.pathname}${to.search ? `/${to.search}` : ''}`;
  const avatar = notification.users && notification.users[0].avatar;
  const icon = notification.data.icon;
  const body = notification.data.body;
  const imageUrl = notification.data.image_url;
  const additionalCopy = notification.data.additional_copy;
  const video = notification.data.video;

  // If the notification has no link, just render wrapper as a div
  let wrapperAsLink = notification.data?.link;
  const unreadClasses = classNames('feed-item-unread-indicator', !read ? 'feed-item-unread-indicator--unread' : false);

  if (
    (notification?.data?.type === WebNotificationType.giveFeedbackReminder && feedbackActionItem) ||
    notLinkWrapperTypes.includes(notification?.data?.type)
  ) {
    wrapperAsLink = false;
  }

  return (
    <FeedItemWrapper
      to={toPath}
      asLink={wrapperAsLink}
      className="feed-item d-flex align-items-center p-2 text-decoration-none text-break"
      onClick={handleItemClick}
    >
      <div className="d-flex flex-fill">
        {avatar && <Avatar avatar={avatar} height={42} classNames="mr-2" />}
        {!avatar && icon && (
          <span className="mr-2">
            <WpIcon name={icon} />
          </span>
        )}
        <div className="d-flex flex-column mr-1 flex-fill">
          <div>
            <span dangerouslySetInnerHTML={{ __html: dompurify.sanitize(body) }} />
            <CreateActionItemLinkContent
              notification={notification}
              onCreateActionItemClick={onCreateActionItemClick}
            />
          </div>
          <div className="small feed-item-date">{moment.unix(notification.sentAt).fromNow()}</div>
          {imageUrl && <img className="feed-item-image w-100 mt-2 mb-1" src={imageUrl} alt="" />}
          {video ? (
            <VideoPlayer
              className="flex-column"
              videoID={video.id}
              sources={[video.mp4Url, video.url]}
              previewImageURL={video.previewImageUrl}
              transcription={video.transcription}
              audioOnly={video.audioOnly}
              withoutTranscription
            />
          ) : null}
          {additionalCopy && <p className="w-100 mt-2 mb-0">{additionalCopy}</p>}
        </div>
      </div>
      <div className="feed-item-unread flex-shrink-0">
        <div className={unreadClasses} />
      </div>
    </FeedItemWrapper>
  );
}

export default memo(FeedItem);
